import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Building2,
  Loader2,
  User,
  Mail,
  Phone,
  MapPin,
  Eye,
  Upload,
  Globe,
  CreditCard,
  FileText,
  Linkedin,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Companies = () => {
  const { isOfficeManager } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Company dialog
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    legal_name: "",
    dba_name: "",
    company_type: "",
    industry: "",
    email: "",
    phone: "",
    phone_secondary: "",
    fax: "",
    website: "",
    primary_contact_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    primary_contact_title: "",
    address: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "Canada",
    billing_address: "",
    billing_city: "",
    billing_state: "",
    billing_postal_code: "",
    billing_country: "",
    business_number: "",
    tax_id: "",
    wcb_number: "",
    payment_terms: "Net 30",
    credit_limit: "",
    tax_exempt: false,
    linkedin_url: "",
    facebook_url: "",
    notes: "",
    internal_notes: "",
  });

  // PM dialog
  const [pmDialogOpen, setPmDialogOpen] = useState(false);
  const [editingPm, setEditingPm] = useState(null);
  const [pmForm, setPmForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_id: "",
    notes: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, pmsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/property-managers`),
      ]);
      setCompanies(companiesRes.data);
      setPropertyManagers(pmsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCompanyDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm({
        name: company.name || "",
        legal_name: company.legal_name || "",
        dba_name: company.dba_name || "",
        company_type: company.company_type || "",
        industry: company.industry || "",
        email: company.email || "",
        phone: company.phone || "",
        phone_secondary: company.phone_secondary || "",
        fax: company.fax || "",
        website: company.website || "",
        primary_contact_name: company.primary_contact_name || "",
        primary_contact_email: company.primary_contact_email || "",
        primary_contact_phone: company.primary_contact_phone || "",
        primary_contact_title: company.primary_contact_title || "",
        address: company.address || "",
        address_line2: company.address_line2 || "",
        city: company.city || "",
        state: company.state || "",
        postal_code: company.postal_code || "",
        country: company.country || "Canada",
        billing_address: company.billing_address || "",
        billing_city: company.billing_city || "",
        billing_state: company.billing_state || "",
        billing_postal_code: company.billing_postal_code || "",
        billing_country: company.billing_country || "",
        business_number: company.business_number || "",
        tax_id: company.tax_id || "",
        wcb_number: company.wcb_number || "",
        payment_terms: company.payment_terms || "Net 30",
        credit_limit: company.credit_limit || "",
        tax_exempt: company.tax_exempt || false,
        linkedin_url: company.linkedin_url || "",
        facebook_url: company.facebook_url || "",
        notes: company.notes || "",
        internal_notes: company.internal_notes || "",
      });
    } else {
      setEditingCompany(null);
      setCompanyForm({
        name: "",
        legal_name: "",
        dba_name: "",
        company_type: "",
        industry: "",
        email: "",
        phone: "",
        phone_secondary: "",
        fax: "",
        website: "",
        primary_contact_name: "",
        primary_contact_email: "",
        primary_contact_phone: "",
        primary_contact_title: "",
        address: "",
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "Canada",
        billing_address: "",
        billing_city: "",
        billing_state: "",
        billing_postal_code: "",
        billing_country: "",
        business_number: "",
        tax_id: "",
        wcb_number: "",
        payment_terms: "Net 30",
        credit_limit: "",
        tax_exempt: false,
        linkedin_url: "",
        facebook_url: "",
        notes: "",
        internal_notes: "",
      });
    }
    setCompanyDialogOpen(true);
  };

  const handleSubmitCompany = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, companyForm);
        toast.success("Company updated");
      } else {
        await axios.post(`${API}/companies`, companyForm);
        toast.success("Company created");
      }
      setCompanyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm("Are you sure you want to archive this company?"))
      return;

    try {
      await axios.delete(`${API}/companies/${id}`);
      toast.success("Company archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive company");
    }
  };

  const handleOpenPmDialog = (pm = null, companyId = "") => {
    if (pm) {
      setEditingPm(pm);
      setPmForm({
        name: pm.name,
        email: pm.email,
        phone: pm.phone || "",
        company_id: pm.company_id,
        notes: pm.notes || "",
      });
    } else {
      setEditingPm(null);
      setPmForm({
        name: "",
        email: "",
        phone: "",
        company_id: companyId,
        notes: "",
      });
    }
    setPmDialogOpen(true);
  };

  const handleSubmitPm = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingPm) {
        await axios.put(`${API}/property-managers/${editingPm.id}`, pmForm);
        toast.success("Property manager updated");
      } else {
        await axios.post(`${API}/property-managers`, pmForm);
        toast.success("Property manager created");
      }
      setPmDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePm = async (id) => {
    if (!window.confirm("Are you sure you want to archive this property manager?"))
      return;

    try {
      await axios.delete(`${API}/property-managers/${id}`);
      toast.success("Property manager archived");
      fetchData();
    } catch (error) {
      toast.error("Failed to archive property manager");
    }
  };

  const filteredCompanies = companies.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPmsForCompany = (companyId) =>
    propertyManagers.filter((pm) => pm.company_id === companyId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="companies-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Companies & Property Managers
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage property management companies and their contacts.
          </p>
        </div>
        {isOfficeManager && (
          <Button onClick={() => handleOpenCompanyDialog()} data-testid="add-company-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="company-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Companies List */}
      <div className="space-y-4">
        {filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No companies found
            </CardContent>
          </Card>
        ) : (
          <Accordion type="multiple" className="space-y-4">
            {filteredCompanies.map((company) => (
              <AccordionItem
                key={company.id}
                value={company.id}
                className="border rounded-xl bg-card"
              >
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-4 text-left">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{company.name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {company.address}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    {/* Company Details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                      {company.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {company.phone}
                        </div>
                      )}
                      {company.accounts_payable_email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {company.accounts_payable_email}
                        </div>
                      )}
                      {company.secondary_address && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {company.secondary_address}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(`/companies/${company.id}`)}
                        data-testid={`view-company-${company.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                      {isOfficeManager && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenCompanyDialog(company)}
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPmDialog(null, company.id)}
                            data-testid={`add-pm-${company.id}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Contact
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteCompany(company.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Archive
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Property Managers */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-muted-foreground">
                        Property Managers
                      </h4>
                      {getPmsForCompany(company.id).length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">
                          No property managers assigned
                        </p>
                      ) : (
                        <div className="grid gap-2">
                          {getPmsForCompany(company.id).map((pm) => (
                            <div
                              key={pm.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                              data-testid={`pm-card-${pm.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-8 w-8 rounded-full bg-secondary/50 flex items-center justify-center">
                                  <User className="h-4 w-4" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{pm.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {pm.email}
                                  </p>
                                </div>
                              </div>
                              {isOfficeManager && (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleOpenPmDialog(pm)}
                                    data-testid={`edit-pm-${pm.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeletePm(pm.id)}
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-pm-${pm.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      {/* Company Dialog */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {editingCompany ? "Edit Company" : "Add Company"}
            </DialogTitle>
            <DialogDescription>
              {editingCompany ? "Update company information" : "Enter the company details below"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCompany} className="space-y-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="address">Address</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
              </TabsList>
              
              <div className="max-h-[45vh] overflow-y-auto pr-2 mt-4">
                {/* Basic Info Tab */}
                <TabsContent value="basic" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-name">Company Name *</Label>
                      <Input
                        id="company-name"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        required
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company-legal-name">Legal Name</Label>
                      <Input
                        id="company-legal-name"
                        value={companyForm.legal_name}
                        onChange={(e) => setCompanyForm({ ...companyForm, legal_name: e.target.value })}
                        placeholder="Full legal business name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-dba">DBA Name</Label>
                      <Input
                        id="company-dba"
                        value={companyForm.dba_name}
                        onChange={(e) => setCompanyForm({ ...companyForm, dba_name: e.target.value })}
                        placeholder="Doing Business As"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Company Type</Label>
                      <Select 
                        value={companyForm.company_type || "none"} 
                        onValueChange={(v) => setCompanyForm({ ...companyForm, company_type: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          <SelectItem value="Corporation">Corporation</SelectItem>
                          <SelectItem value="LLC">LLC</SelectItem>
                          <SelectItem value="Partnership">Partnership</SelectItem>
                          <SelectItem value="Sole Proprietor">Sole Proprietor</SelectItem>
                          <SelectItem value="Non-Profit">Non-Profit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company-industry">Industry</Label>
                      <Input
                        id="company-industry"
                        value={companyForm.industry}
                        onChange={(e) => setCompanyForm({ ...companyForm, industry: e.target.value })}
                        placeholder="e.g., Property Management"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company-website">Website</Label>
                      <Input
                        id="company-website"
                        value={companyForm.website}
                        onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company-notes">Notes</Label>
                    <Textarea
                      id="company-notes"
                      value={companyForm.notes}
                      onChange={(e) => setCompanyForm({ ...companyForm, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                </TabsContent>
                
                {/* Contact Tab */}
                <TabsContent value="contact" className="space-y-4 mt-0">
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <h4 className="font-medium">Primary Contact</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          value={companyForm.primary_contact_name}
                          onChange={(e) => setCompanyForm({ ...companyForm, primary_contact_name: e.target.value })}
                          placeholder="John Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={companyForm.primary_contact_title}
                          onChange={(e) => setCompanyForm({ ...companyForm, primary_contact_title: e.target.value })}
                          placeholder="Property Manager"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={companyForm.primary_contact_email}
                          onChange={(e) => setCompanyForm({ ...companyForm, primary_contact_email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={companyForm.primary_contact_phone}
                          onChange={(e) => setCompanyForm({ ...companyForm, primary_contact_phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Main Email</Label>
                      <Input
                        type="email"
                        value={companyForm.email}
                        onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Main Phone</Label>
                      <Input
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Secondary Phone</Label>
                      <Input
                        value={companyForm.phone_secondary}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone_secondary: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fax</Label>
                      <Input
                        value={companyForm.fax}
                        onChange={(e) => setCompanyForm({ ...companyForm, fax: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>LinkedIn URL</Label>
                      <Input
                        value={companyForm.linkedin_url}
                        onChange={(e) => setCompanyForm({ ...companyForm, linkedin_url: e.target.value })}
                        placeholder="https://linkedin.com/company/..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Facebook URL</Label>
                      <Input
                        value={companyForm.facebook_url}
                        onChange={(e) => setCompanyForm({ ...companyForm, facebook_url: e.target.value })}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                  </div>
                </TabsContent>
                
                {/* Address Tab */}
                <TabsContent value="address" className="space-y-4 mt-0">
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <h4 className="font-medium">Main Address</h4>
                    <div className="space-y-2">
                      <Label>Street Address</Label>
                      <Input
                        value={companyForm.address}
                        onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                        data-testid="company-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={companyForm.address_line2}
                        onChange={(e) => setCompanyForm({ ...companyForm, address_line2: e.target.value })}
                        placeholder="Suite, Unit, Building, etc."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={companyForm.city}
                          onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Province/State</Label>
                        <Input
                          value={companyForm.state}
                          onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Postal Code</Label>
                        <Input
                          value={companyForm.postal_code}
                          onChange={(e) => setCompanyForm({ ...companyForm, postal_code: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input
                          value={companyForm.country}
                          onChange={(e) => setCompanyForm({ ...companyForm, country: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                    <h4 className="font-medium">Billing Address (if different)</h4>
                    <div className="space-y-2">
                      <Label>Billing Street Address</Label>
                      <Input
                        value={companyForm.billing_address}
                        onChange={(e) => setCompanyForm({ ...companyForm, billing_address: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={companyForm.billing_city}
                          onChange={(e) => setCompanyForm({ ...companyForm, billing_city: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Province/State</Label>
                        <Input
                          value={companyForm.billing_state}
                          onChange={(e) => setCompanyForm({ ...companyForm, billing_state: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Postal Code</Label>
                        <Input
                          value={companyForm.billing_postal_code}
                          onChange={(e) => setCompanyForm({ ...companyForm, billing_postal_code: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Input
                          value={companyForm.billing_country}
                          onChange={(e) => setCompanyForm({ ...companyForm, billing_country: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Financial Tab */}
                <TabsContent value="financial" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Number</Label>
                      <Input
                        value={companyForm.business_number}
                        onChange={(e) => setCompanyForm({ ...companyForm, business_number: e.target.value })}
                        placeholder="Business registration #"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tax ID / GST/HST #</Label>
                      <Input
                        value={companyForm.tax_id}
                        onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>WCB Number</Label>
                      <Input
                        value={companyForm.wcb_number}
                        onChange={(e) => setCompanyForm({ ...companyForm, wcb_number: e.target.value })}
                        placeholder="Workers Comp Board #"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Terms</Label>
                      <Select 
                        value={companyForm.payment_terms} 
                        onValueChange={(v) => setCompanyForm({ ...companyForm, payment_terms: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Net 15">Net 15</SelectItem>
                          <SelectItem value="Net 30">Net 30</SelectItem>
                          <SelectItem value="Net 45">Net 45</SelectItem>
                          <SelectItem value="Net 60">Net 60</SelectItem>
                          <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Credit Limit ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={companyForm.credit_limit}
                        onChange={(e) => setCompanyForm({ ...companyForm, credit_limit: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tax Exempt</Label>
                      <div className="flex items-center h-10 gap-3">
                        <Switch
                          checked={companyForm.tax_exempt}
                          onCheckedChange={(checked) => setCompanyForm({ ...companyForm, tax_exempt: checked })}
                        />
                        <span className="text-sm text-muted-foreground">
                          {companyForm.tax_exempt ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Internal Notes</Label>
                    <Textarea
                      value={companyForm.internal_notes}
                      onChange={(e) => setCompanyForm({ ...companyForm, internal_notes: e.target.value })}
                      rows={3}
                      placeholder="Internal notes (not visible to client)"
                    />
                  </div>
                </TabsContent>
              </div>
            </Tabs>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCompanyDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-company-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCompany ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PM Dialog */}
      <Dialog open={pmDialogOpen} onOpenChange={setPmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPm ? "Edit Property Manager" : "Add Property Manager"}
            </DialogTitle>
            <DialogDescription>
              Enter the property manager details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitPm} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pm-name">Full Name *</Label>
              <Input
                id="pm-name"
                value={pmForm.name}
                onChange={(e) =>
                  setPmForm({ ...pmForm, name: e.target.value })
                }
                required
                data-testid="pm-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pm-email">Email *</Label>
              <Input
                id="pm-email"
                type="email"
                value={pmForm.email}
                onChange={(e) =>
                  setPmForm({ ...pmForm, email: e.target.value })
                }
                required
                data-testid="pm-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pm-phone">Phone</Label>
              <Input
                id="pm-phone"
                value={pmForm.phone}
                onChange={(e) =>
                  setPmForm({ ...pmForm, phone: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pm-notes">Notes</Label>
              <Textarea
                id="pm-notes"
                value={pmForm.notes}
                onChange={(e) =>
                  setPmForm({ ...pmForm, notes: e.target.value })
                }
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPmDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-pm-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPm ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Companies;
